import { useContext, useEffect, useState } from "react";
import { UserDataContext } from "../context/User";
import axios from "axios";
import { Upload, CheckCircle } from "lucide-react";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAllowedImage = (file) => {
  const extension = `.${(file?.name || "").split(".").pop()?.toLowerCase()}`;
  return ALLOWED_IMAGE_TYPES.includes(file?.type) && ALLOWED_IMAGE_EXTENSIONS.includes(extension);
};

const normalizeValue = (value) => String(value || "").trim().toLowerCase();
const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []).map(normalizeValue).filter(Boolean);
const displayDate = (value) => new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const getQuestRestrictionReasons = (challenge, user) => {
  if (!challenge) return [];
  const reasons = [];
  const now = new Date();
  if (challenge.startDate && now < new Date(challenge.startDate)) reasons.push(`Submissions open on ${displayDate(challenge.startDate)}.`);
  if (challenge.endDate) {
    const end = new Date(challenge.endDate);
    if (end.getUTCHours() === 0 && end.getUTCMinutes() === 0) end.setTime(end.getTime() + 24 * 60 * 60 * 1000 - 1);
    if (now > end) reasons.push(`Submissions closed on ${displayDate(challenge.endDate)}.`);
  }
  const allowedTiers = asList(challenge.accessibility);
  const memberTier = normalizeValue(user?.membershipTier?.key || user?.primaryRoleKey || "free");
  if (allowedTiers.length && !allowedTiers.includes(memberTier)) reasons.push(`This quest is limited to these member levels: ${allowedTiers.join(", ")}. Your current level is ${memberTier}.`);

  const rules = challenge.eligibilityRules || {};
  const minAge = Number(rules.minAge);
  const maxAge = Number(rules.maxAge);
  if ((minAge > 0 || maxAge > 0)) {
    const birth = user?.demographic?.dateOfBirth ? new Date(user.demographic.dateOfBirth) : null;
    let age = null;
    if (birth && !Number.isNaN(birth.getTime())) {
      age = now.getFullYear() - birth.getFullYear();
      if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1;
    }
    if (age === null) reasons.push("This quest has an age restriction. Add your date of birth to your profile before submitting.");
    else if (minAge > 0 && age < minAge) reasons.push(`You must be at least ${minAge} years old to enter this quest.`);
    else if (maxAge > 0 && age > maxAge) reasons.push(`You must be ${maxAge} years old or younger to enter this quest.`);
  }
  if (rules.requiresUrm === true && user?.demographic?.urmIdentification !== true) reasons.push("This quest is limited to members who identify as part of an underrepresented minority (URM).");
  [["allowedLocationTypes", "locationType", "location type"], ["allowedGenderIdentities", "genderIdentity", "gender identity"], ["allowedEthnicities", "ethnicity", "ethnicity"]].forEach(([ruleKey, profileKey, label]) => {
    const allowed = asList(rules[ruleKey]);
    const actual = normalizeValue(user?.demographic?.[profileKey]);
    if (allowed.length && (!actual || !allowed.includes(actual))) reasons.push(`This quest is restricted by ${label}. Eligible values: ${allowed.join(", ")}.`);
  });
  return reasons;
};
const QuestForm = ({ onSuccess, onError, challenges = [], initialChallengeId = "", isFree = false, freeQuestLimitReached = false, freeExtraSubmissionFeeCents = 1000 }) => {
  const { userData } = useContext(UserDataContext);
  const [title, setTitle]           = useState("");
  const [description, setDescription] = useState("");
  const [member_bio, setMember_bio] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [file, setFile]             = useState(null);
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedChallenge = challenges.find((item) => item._id === challengeId);
  const selectedFee = selectedChallenge?.entryFeeCents || 0;
  const freeExtraSubmissionFee = (Number(freeExtraSubmissionFeeCents) || 0) / 100;
  const freeEntryCoversSelectedFee = isFree && !freeQuestLimitReached && selectedFee > 0;
  const freeUserPaysExtraSubmissionFee = isFree && freeQuestLimitReached;
  const restrictionReasons = getQuestRestrictionReasons(selectedChallenge, userData);

  useEffect(() => {
    if (initialChallengeId && challenges.some((item) => item._id === initialChallengeId)) {
      setChallengeId(initialChallengeId);
    }
  }, [initialChallengeId, challenges]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      if (!isAllowedImage(f)) {
        setFile(null);
        setPreview(null);
        setFieldErrors((prev) => ({
          ...prev,
          file: "Only JPG, PNG, or WEBP image files can be uploaded.",
        }));
        e.target.value = "";
        return;
      }

      setFile(f);
      setPreview(URL.createObjectURL(f));
      setFieldErrors((prev) => ({
        ...prev,
        file: "",
      }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!challengeId || !selectedChallenge) {
      errors.challengeId = "Please select the quest or event this submission is for.";
    }
    if (restrictionReasons.length) errors.challengeId = restrictionReasons.join(" ");
    if (!title.trim())       errors.title       = "Title is required.";
    if (!description.trim()) errors.description = "Description is required.";
    if (!file)               errors.file        = "Please upload an image for your submission.";
    if (file && !isAllowedImage(file)) {
      errors.file = "Only JPG, PNG, or WEBP image files can be uploaded.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("pfp", file);
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("member_bio", member_bio.trim());
      formData.append("challengeId", challengeId);
      formData.append("challengeTitle", selectedChallenge.title || "");
      formData.append("challengeCategory", selectedChallenge.category || "");

      const res = await axios.post("/api/quest/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (res.data.success) {
        if (res.data.checkoutUrl) {
          window.location.href = res.data.checkoutUrl;
          return;
        }
        setTitle(""); setDescription(""); setMember_bio("");
        setChallengeId(""); setFile(null); setPreview(null); setFieldErrors({});
        onSuccess?.();
      } else {
        const msg = res.data.message || "Submission failed. Please try again.";
        onError?.(msg);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Submission failed. Please try again.";
      if (/upload|image|file/i.test(msg)) {
        setFieldErrors((prev) => ({ ...prev, file: msg }));
      }
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-8 max-w-4xl mx-auto shadow-lg border border-gray-100">
      <h2 className="font-cinzel text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
        Quest Submission
      </h2>
      <p className="font-outfit text-gray-500 text-sm text-center mb-8">
        Share your creative work and connect it to the current quest theme.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Image Upload */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Submission Image <span className="text-red-500">*</span>
          </label>
          <label
            htmlFor="questImage"
            className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors
              ${fieldErrors.file ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50/40"}`}
          >
            <input type="file" id="questImage" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
            {preview ? (
              <img src={preview} alt="Preview" className="h-48 w-auto object-contain rounded-xl shadow-sm mb-3" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center mb-3">
                <Upload size={20} className="text-gray-400" strokeWidth={1.5} />
              </div>
            )}
            <p className="font-outfit text-sm text-gray-500">
              {preview ? "Click to change image" : "Click to upload your submission image"}
            </p>
          </label>
          <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-outfit text-xs text-gray-400">
              Accepted formats: JPG, PNG, WEBP. File size is shown before submit.
            </p>
            {file && (
              <p className="font-outfit text-xs font-medium text-gray-500">
                Selected: {file.name} ({formatFileSize(file.size)})
              </p>
            )}
          </div>
          {fieldErrors.file && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.file}</p>}
        </div>

        {/* Member ID (read-only) */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Quest / Event <span className="text-red-500">*</span>
          </label>
          <select
            value={challengeId}
            onChange={(e) => {
              setChallengeId(e.target.value);
              setFieldErrors((prev) => ({ ...prev, challengeId: "" }));
            }}
            className={`w-full rounded-full border px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition ${
              fieldErrors.challengeId ? "border-red-300 bg-red-50" : "border-gray-200"
            }`}
          >
            <option value="">Select quest or event</option>
            {challenges.map((challenge) => (
              <option key={challenge._id} value={challenge._id}>
                {challenge.title}
                {challenge.category === "events" && challenge.entryFeeCents > 0
                  ? ` - $${(challenge.entryFeeCents / 100).toFixed(2)} entry`
                  : ""}
              </option>
            ))}
          </select>
          {fieldErrors.challengeId && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.challengeId}</p>}
          {restrictionReasons.length > 0 && (
            <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
              <p className="font-semibold">You cannot submit to this quest:</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">{restrictionReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
            </div>
          )}          {freeUserPaysExtraSubmissionFee && selectedFee <= 0 && (
            <p className="mt-2 font-outfit text-xs text-amber-600">
              {`Your included free quest entry has been used. This submission requires a $${freeExtraSubmissionFee.toFixed(2)} payment after it is saved.`}
            </p>
          )}
          {selectedFee > 0 && (
            <p className="mt-2 font-outfit text-xs text-amber-600">
              {freeUserPaysExtraSubmissionFee
                ? `Your included free quest entry has been used. This submission requires a $${freeExtraSubmissionFee.toFixed(2)} payment after it is saved.`
                : freeEntryCoversSelectedFee
                  ? `Your included free quest entry covers this $${(selectedFee / 100).toFixed(2)} event submission. Payment is required after your free entry is used.`
                  : `This event requires a $${(selectedFee / 100).toFixed(2)} entry payment. You will be redirected to checkout after the submission is saved.`}
            </p>
          )}
        </div>

        {/* Member ID (read-only) */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Member ID
          </label>
          <input
            value={userData?.memberId || "—"}
            disabled
            className="w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-outfit text-gray-400 cursor-not-allowed"
          />
        </div>

        {/* Title */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Title of Work <span className="text-red-500">*</span>
          </label>
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); setFieldErrors((p) => ({ ...p, title: "" })); }}
            type="text"
            className={`w-full rounded-full border px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition
              ${fieldErrors.title ? "border-red-300 bg-red-50" : "border-gray-200"}`}
            placeholder="Enter the title of your piece"
          />
          {fieldErrors.title && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Describe Theme Fit <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setFieldErrors((p) => ({ ...p, description: "" })); }}
            rows={5}
            className={`w-full rounded-2xl border px-4 py-3 text-sm font-outfit text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-300 transition
              ${fieldErrors.description ? "border-red-300 bg-red-50" : "border-gray-200"}`}
            placeholder="Share how your work connects to the theme..."
          />
          {fieldErrors.description && <p className="font-outfit text-xs text-red-500 mt-1">{fieldErrors.description}</p>}
        </div>

        {/* Member Bio */}
        <div>
          <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Member Bio <span className="text-gray-400 font-normal normal-case">(optional)</span>
          </label>
          <input
            value={member_bio}
            onChange={(e) => setMember_bio(e.target.value)}
            type="text"
            className="w-full rounded-full border border-gray-200 px-4 py-3 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition"
            placeholder="A short bio about yourself..."
          />
        </div>

        {/* Submit */}
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={loading || restrictionReasons.length > 0}
            className="inline-flex items-center gap-2 px-10 py-3 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm shadow-md hover:scale-105 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Submitting...</>
            ) : (
              <><CheckCircle size={16} strokeWidth={2.5} /> {freeUserPaysExtraSubmissionFee ? "Save & Pay Submission Fee" : freeEntryCoversSelectedFee ? "Submit Free Entry" : selectedFee > 0 ? "Save & Pay Entry Fee" : "Submit Entry"}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestForm;
